import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import logoIcon from "../assets/pnrbet-icon.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(data.message || "Password reset! You can sign in now.");
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-surface-1 px-6 py-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div className="grain" />
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-sm rounded-2xl border border-surface-border bg-surface-3 p-8 text-white shadow-[var(--elev-2)]">
        <div className="mb-6 flex items-center gap-2">
          <img src={logoIcon} alt="" className="h-[45px] w-[45px] rounded-md" />
          <span className="font-display text-xl font-extrabold tracking-tighter text-white">PRYMEBET</span>
        </div>

        {!token ? (
          <div>
            <h2 className="font-display text-2xl font-black tracking-tighter text-white">Invalid link</h2>
            <p className="mt-2 text-sm text-meta">This reset link is missing or broken. Please request a new one from the sign-in page.</p>
            <Button data-testid="reset-back-login" onClick={() => navigate("/login")} className="mt-5 h-12 w-full rounded-full border border-surface-border bg-surface-5 font-bold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring">Back to sign in</Button>
          </div>
        ) : done ? (
          <div>
            <h2 className="font-display text-2xl font-black tracking-tighter text-white">Password updated</h2>
            <p className="mt-2 text-sm text-meta">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h2 className="mb-2 font-display text-2xl font-black tracking-tighter text-white">Choose a new password</h2>
            <p className="mb-6 text-sm text-meta">Enter a new password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="np">New password</Label>
                <Input id="np" type="password" data-testid="reset-password-input" autoComplete="new-password"
                  enterKeyHint="next" value={password}
                  onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 focus-ring focus-visible:border-pnr-green" placeholder="••••••••" />
              </div>
              <div>
                <Label htmlFor="cp">Confirm password</Label>
                <Input id="cp" type="password" data-testid="reset-confirm-input" autoComplete="new-password"
                  enterKeyHint="go" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 focus-ring focus-visible:border-pnr-green" placeholder="••••••••" />
              </div>
              <Button type="submit" data-testid="reset-submit-button" disabled={loading}
                className="h-12 w-full rounded-full bg-pnr-green font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60">
                {loading ? "Please wait..." : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
